import {
	FRAGMENTS_DIRECTORY,
	INBOX_DIRECTORY,
	PROMPT_DIRECTORY,
	SNIPPETS_DIRECTORY,
	TEMPLATES_DIRECTORY,
} from "@app/config/const";
import { getExecutingEnvironment } from "@app/resolveModule/env";
import Path from "@core/FileProvider/Path/Path";
import type { Article } from "@core/FileStructue/Article/Article";
import ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import { ArticleProviders, type ArticleProviderType } from "@ext/articleProvider/logic/ArticleProvider";
import type { ParseSpec } from "@ext/markdown/core/edit/logic/Prosemirror/from_markdown";
import type PrivateParserContext from "@ext/markdown/core/Parser/ParserContext/PrivateParserContext";
import { parse } from "@ext/markdown/elements/image/render/logic/imageTransformer";
import linkCreator from "@ext/markdown/elements/link/render/logic/linkCreator";
import { hasScopeSeparator } from "@ext/versioning/addScopeToPath";
import { replacePathIfNeeded } from "../../../../../../../apps/cli/src/logic/NavigationUtils";

const dirs = [FRAGMENTS_DIRECTORY, SNIPPETS_DIRECTORY, TEMPLATES_DIRECTORY, PROMPT_DIRECTORY, INBOX_DIRECTORY] as const;

const providerMapping: Record<(typeof dirs)[number], ArticleProviderType> = {
	[FRAGMENTS_DIRECTORY]: "fragment",
	[SNIPPETS_DIRECTORY]: "fragment",
	[TEMPLATES_DIRECTORY]: "template",
	[PROMPT_DIRECTORY]: "prompt",
	[INBOX_DIRECTORY]: "inbox",
};

const getProviderFromPath = (dir: (typeof dirs)[number]): ArticleProviderType | null => {
	if (!ArticleProviders) return null;
	return providerMapping[dir] || null;
};

const getIdAndProvider = (article: Article) => {
	const parent = article.parent;
	if (parent) return { id: null, provider: null };
	const id = article.ref.path.name;
	const provider = getProviderFromPath(article.ref.path.parentDirectoryPath.name as (typeof dirs)[number]);
	if (!provider) return { id: null, provider: null };
	return { id, provider };
};

export const getRenderSrc = (context: PrivateParserContext, src: string) => {
	const env = getExecutingEnvironment();
	const catalog = context.getCatalog();

	if (env === "static" || env === "cli") {
		const path = context.getResourceManager().getAbsolutePath(new Path(src)).value;
		if (env === "static") return path;
		if (env === "cli") return replacePathIfNeeded(path, catalog);
	}
	if (env !== "next") return;
	// A portal finds a catalog read from a git scope by its name only when it is a published version.
	if (hasScopeSeparator(catalog.name) && !catalog.props.resolvedVersion) return;

	const article = context.getArticle();
	const { id, provider } = getIdAndProvider(article);
	const page = context.getEmbeddingRoot();
	return new ApiUrlCreator(context.getBasePath().value, page.getCatalog().name, page.getArticle().ref.path.value)
		.getArticleResource(src, undefined, catalog.name, id, provider)
		.toString();
};

function imageToken(context: PrivateParserContext): ParseSpec {
	return {
		node: "image",
		getAttrs: (tok) => {
			const isExternalLink = linkCreator.isExternalLink(tok.attrs.src);
			if (!isExternalLink) context.getResourceManager().set(new Path(tok.attrs.src));

			const renderSrc = isExternalLink ? tok.attrs.src : getRenderSrc(context, tok.attrs.src);
			const { crop, objects, scale } = parse(
				tok.attrs.crop ?? "0,0,100,100",
				// A tag carries its attributes on the token, a Markdown image in an attribute list.
				(tok?.attrGet ? tok.attrGet("scale") : tok.attrs.scale) ?? null,
				tok.attrs.objects ?? "[]",
				tok.attrs.width,
				tok.attrs.height,
				tok.attrs.float,
			);

			return {
				src: tok?.attrGet ? tok.attrGet("src") : tok.attrs.src,
				title: tok?.attrGet ? tok.attrGet("title") || null : tok.attrs.title,
				href: (tok?.attrGet ? tok.attrGet("href") : tok.attrs.href) || null,
				alt: tok.children ? tok.children[0]?.content || null : tok.attrs.alt,
				crop: crop,
				scale: scale,
				objects: objects,
				width: tok?.attrGet ? tok.attrGet("width") : tok.attrs.width,
				height: tok?.attrGet ? tok.attrGet("height") : tok.attrs.height,
				renderSrc,
			};
		},
	};
}

export default imageToken;
